<template>
  <span class="view-string" :id="field.key">
    <!-- Campo name/title con enlace al item -->
    <span
      v-if="
        (field.key === 'name' || field.key === 'title') &&
        item &&
        (item._modelName || modelName) &&
        item._id &&
        !isTextareaHtml
      "
      class="d-inline-flex align-items-center gap-1"
    >
      <span
        class="cursor-pointer text-primary text-decoration-underline"
        @click.stop="goToItem(item._id, item._modelName || modelName)"
        style="cursor: pointer;"
      >
        {{ displayValue }}
      </span>
      <a
        :href="getItemUrl(item._id, item._modelName || modelName)"
        target="_blank"
        rel="noopener noreferrer"
        @click.stop
        class="text-muted text-decoration-none"
        :title="$t('Open in new tab')"
        style="font-size: 0.8em;"
      >
        <i class="fas fa-external-link-alt"></i>
      </a>
      <MissingPrepaymentWarningIcon
        v-if="showMissingPrepaymentWarning"
        :record="item"
      />
      <MissingRequiredWarningIcon :item="item" />
    </span>

    <!-- Campo blnumber con enlace especial -->
    <span
      v-else-if="field.key === 'blnumber' && displayValue && !isTextareaHtml"
      class="view-string d-inline-flex align-items-center flex-wrap gap-1"
    >
      <a
        href="#"
        @click.prevent="goToBlnumber"
        class="text-primary text-decoration-none"
      >
        {{ displayValue }}
      </a>
      <FindTeuTrackingViewButton
        v-if="!isGroupModel"
        :field-key="field.key"
        :raw-value="displayValue"
        :model-name="modelName"
      />
      <a
        v-if="!isGroupModel && !isFindTeuConfiguredTrackingField"
        target="_blank"
        rel="noopener noreferrer"
        :href="'/tracking/' + encodeURIComponent(String(displayValue))"
        class="btn btn-sm btn-outline-primary ms-1 py-0 px-2"
        :title="$t('findTeu.viewTrackingFromField')"
      >
        <i class="fas fa-eye" aria-hidden="true"></i>
      </a>
    </span>

    <!-- Campo string marcado como link (definición: field.link) -->
    <span
      v-else-if="isLinkField && linkEntries.length && !isTextareaHtml"
      class="d-flex flex-column align-items-start gap-1"
    >
      <span
        v-for="(entry, idx) in linkEntries"
        :key="idx"
        class="d-inline-flex align-items-center gap-1"
      >
        <a
          :href="entry.href"
          class="text-primary text-decoration-underline"
          rel="noopener noreferrer"
        >
          {{ entry.label }}
        </a>
        <a
          :href="entry.href"
          target="_blank"
          rel="noopener noreferrer"
          class="text-muted text-decoration-none"
          :title="$t('Open in new tab')"
          style="font-size: 0.8em;"
        >
          <i class="fas fa-external-link-alt"></i>
        </a>
      </span>
    </span>

    <!-- Campo de texto normal -->
    <span v-else class="d-inline-flex align-items-center flex-wrap gap-1">
      <template v-if="field.type == 'textarea'">
        <template v-if="isTextareaHtml">
          <div
            v-if="field.isArray && Array.isArray(displayValue)"
            class="view-textarea-html mb-0"
            :class="{ 'view-textarea-html--list-compact': compactTextarea }"
            :title="compactTextarea ? textareaPlainTitle : null"
          >
            <div
              v-for="(chunk, htmlIdx) in displayValue"
              :key="htmlIdx"
              class="view-textarea-html-item"
              v-html="chunk != null ? String(chunk) : ''"
            />
          </div>
          <div
            v-else
            class="view-textarea-html mb-0"
            :class="{ 'view-textarea-html--list-compact': compactTextarea }"
            :title="compactTextarea ? textareaPlainTitle : null"
            v-html="textareaHtmlScalar"
          />
        </template>
        <template v-else>
          <div
            v-if="field.isArray && Array.isArray(displayValue)"
            class="view-textarea-pre mb-0"
            :class="{ 'view-textarea-pre--list-compact': compactTextarea }"
            :title="compactTextarea ? textareaPlainTitle : null"
          >
            {{ arrayAsText || "" }}
          </div>
          <div
            v-else
            class="view-textarea-pre mb-0"
            :class="{ 'view-textarea-pre--list-compact': compactTextarea }"
            :title="compactTextarea ? textareaPlainTitle : null"
          >
            {{ displayValue || "" }}
          </div>
        </template>
      </template>
      <template v-else>
        <span v-if="field.isArray && Array.isArray(displayValue)">
          {{ displayValue.join(", ") || "" }}
        </span>
        <span v-else>
          {{ displayValue || "" }}
        </span>
      </template>
      <FindTeuTrackingViewButton
        v-if="!isGroupModel && field.type !== 'textarea'"
        :field-key="field.key"
        :raw-value="displayValue"
        :model-name="modelName"
      />
    </span>
  </span>
</template>

<script>
import FindTeuTrackingViewButton from "@/components/findTeu/FindTeuTrackingViewButton.vue";
import MissingPrepaymentWarningIcon from "@/components/gotrading/common/MissingPrepaymentWarningIcon.vue";
import MissingRequiredWarningIcon from "@/components/gotrading/common/MissingRequiredWarningIcon.vue";
import { findSourceForField } from "@/utils/findTeuAutoTrackingSources";
import { buildDynamicModelItemViewPath } from "@/utils/dynamicModelPaths";
import {
  hasMissingAssociatedPrepayment,
  isProformOrShippingModelName,
} from "@/utils/missingPrepaymentWarning";
import {
  getFieldTranslatableLocales,
  getTranslatedFieldValue,
  getTranslatedFieldValueFromItem,
  isFieldTranslatable,
} from "@/utils/fieldValueI18n";

export default {
  name: "ViewFieldText",
  components: {
    FindTeuTrackingViewButton,
    MissingPrepaymentWarningIcon,
    MissingRequiredWarningIcon,
  },
  inject: {
    findTeuAutoTrackingContext: { default: null },
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Array, Object],
      default: null,
    },
    item: {
      type: Object,
      default: null,
    },
    modelName: {
      type: String,
      default: "",
    },
    isGroupModel: {
      type: Boolean,
      default: false,
    },
    /** En tablas (p. ej. dynamic-model): limita altura del textarea en vista */
    compactTextarea: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    displayValue() {
      const loc =
        this.$i18n && this.$i18n.locale
          ? String(this.$i18n.locale).split("-")[0]
          : "es";
      if (isFieldTranslatable(this.field) && this.item) {
        return getTranslatedFieldValueFromItem(
          this.item,
          this.field.key,
          loc,
          getFieldTranslatableLocales(this.field)
        );
      }
      const raw =
        this.value !== null && this.value !== undefined && this.value !== ""
          ? this.value
          : this.field.defaultValue !== undefined &&
            this.field.defaultValue !== null
          ? this.field.defaultValue
          : "";
      if (raw === "" || raw === null || raw === undefined) return "";
      return getTranslatedFieldValue(
        raw,
        loc,
        getFieldTranslatableLocales(this.field)
      );
    },
    isTextareaHtml() {
      if (this.field.type !== "textarea") return false;
      const h = this.field.html;
      return h === true || h === "true" || h === 1 || h === "1";
    },
    textareaHtmlScalar() {
      const v = this.displayValue;
      if (v === null || v === undefined) return "";
      return String(v);
    },
    arrayAsText() {
      const v = this.displayValue;
      if (!Array.isArray(v) || v.length === 0) return "";
      return v.map((x) => (x != null ? String(x) : "")).join("\n\n");
    },
    isLinkField() {
      const l = this.field.link;
      return l === true || l === "true" || l === 1 || l === "1";
    },
    textareaPlainTitle() {
      if (this.field.type !== "textarea") return "";
      if (this.isTextareaHtml) {
        const v = this.displayValue;
        if (this.field.isArray && Array.isArray(v)) {
          return v.map((chunk) => this.stripHtml(chunk)).filter(Boolean).join("\n\n");
        }
        return this.stripHtml(v);
      }
      if (this.field.isArray && Array.isArray(this.displayValue)) {
        return this.arrayAsText || "";
      }
      const v = this.displayValue;
      return v !== null && v !== undefined ? String(v) : "";
    },
    isFindTeuConfiguredTrackingField() {
      const ctx = this.findTeuAutoTrackingContext;
      const rule = ctx && ctx.rule;
      if (!rule || !this.field?.key) return false;
      const mn = String(this.modelName || ctx.modelName || "").trim();
      if (!mn || String(ctx.modelName || "").toLowerCase() !== mn.toLowerCase()) {
        return false;
      }
      return !!findSourceForField(rule, this.field.key);
    },
    linkEntries() {
      if (!this.isLinkField) return [];
      const raw = this.displayValue;
      const parts = Array.isArray(raw)
        ? raw.map((x) => (x != null ? String(x).trim() : "")).filter(Boolean)
        : raw !== null && raw !== undefined && String(raw).trim() !== ""
          ? [String(raw).trim()]
          : [];
      return parts.map((label) => ({
        label,
        href: this.normalizeUrlHref(label),
      }));
    },
    showMissingPrepaymentWarning() {
      const model = this.item && (this.item._modelName || this.modelName);
      if (!isProformOrShippingModelName(model)) return false;
      if (this.field.key !== "name" && this.field.key !== "title") return false;
      return hasMissingAssociatedPrepayment(this.item);
    },
  },
  methods: {
    stripHtml(value) {
      if (value === null || value === undefined) return "";
      const s = String(value);
      if (typeof document === "undefined") return s.replace(/<[^>]+>/g, " ");
      const d = document.createElement("div");
      d.innerHTML = s;
      const t = (d.textContent || d.innerText || "").trim();
      return t || s.replace(/<[^>]+>/g, " ").trim();
    },
    normalizeUrlHref(value) {
      const s = String(value || "").trim();
      if (!s) return "#";
      if (/^https?:\/\//i.test(s)) return s;
      if (/^mailto:/i.test(s) || /^tel:/i.test(s)) return s;
      if (s.startsWith("//")) return s;
      if (s.startsWith("/") || s.startsWith("#")) return s;
      return `https://${s}`;
    },
    goToItem(itemId, modelName) {
      if (!itemId || !modelName) return;
      const title =
        this.item && (this.item.name || this.item.title)
          ? this.item.name || this.item.title
          : "";
      const url = buildDynamicModelItemViewPath(modelName, itemId, title);
      this.$router.push(url).catch((err) => {
        if (err.name !== "NavigationDuplicated") {
          console.error("Error navigating to item:", err);
        }
      });
    },
    getItemUrl(itemId, modelName) {
      if (!itemId || !modelName) return "#";
      const title =
        this.item && (this.item.name || this.item.title)
          ? this.item.name || this.item.title
          : "";
      return buildDynamicModelItemViewPath(modelName, itemId, title);
    },
    goToBlnumber() {
      this.$emit("go-to-blnumber", this.displayValue);
    },
  },
};
</script>

<style scoped>
.view-string {
  font-weight: 500;
}

.view-textarea-pre {
  font-family: inherit;
  font-size: inherit;
  font-weight: 500;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 100%;
}

.view-textarea-pre--list-compact {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  max-width: 100%;
}

.view-textarea-html {
  font-weight: 500;
  max-width: 100%;
}

.view-textarea-html--list-compact {
  max-height: 4.5em;
  overflow: hidden;
}

.view-textarea-html-item + .view-textarea-html-item {
  margin-top: 0.5rem;
}

.view-textarea-html :deep(img),
.view-textarea-html-item :deep(img) {
  max-width: 100%;
  height: auto;
}

.view-string a:hover {
  text-decoration: underline !important;
}
</style>

